import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useContactsList } from '@/hooks/useContacts';

export interface Step2Data {
  recipientMode: 'csv' | 'contacts';
  csvFile: File | null;
  csvPhones: string[];
  csvParsedHeaders: string[];
  csvParsedRows: string[][];
  phoneCol: string;
  nameCol: string;
  selectedContactIds: string[];
}

interface Props {
  data: Step2Data;
  onChange: (patch: Partial<Step2Data>) => void;
  onNext: () => void;
  onBack: () => void;
}

const TABS = [
  { key: 'csv' as const, label: 'Upload CSV' },
  { key: 'contacts' as const, label: 'Contatos salvos' },
];

export function Step2Recipients({ data, onChange, onNext, onBack }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: contactsData, isLoading } = useContactsList({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });
  const contacts = contactsData?.data ?? [];

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const [headerRow, ...dataRows] = results.data;
        if (!headerRow) return;
        const phoneCand = headerRow.find((h) =>
          ['phone', 'telefone', 'fone', 'celular', 'whatsapp'].includes(h.toLowerCase())
        ) ?? headerRow[0] ?? '';
        const phoneIdx = headerRow.indexOf(phoneCand);
        const phones = dataRows.map((r) => r[phoneIdx] ?? '').filter(Boolean);
        onChange({
          csvFile: file,
          csvParsedHeaders: headerRow,
          csvParsedRows: dataRows,
          phoneCol: phoneCand,
          nameCol: '',
          csvPhones: phones,
        });
      },
    });
  }

  function handlePhoneColChange(col: string) {
    if (!data.csvParsedRows.length) return;
    const idx = data.csvParsedHeaders.indexOf(col);
    const phones = data.csvParsedRows.map((r) => r[idx] ?? '').filter(Boolean);
    onChange({ phoneCol: col, csvPhones: phones });
  }

  function toggleContact(id: string) {
    const ids = data.selectedContactIds.includes(id)
      ? data.selectedContactIds.filter((x) => x !== id)
      : [...data.selectedContactIds, id];
    onChange({ selectedContactIds: ids });
  }

  function handleNext() {
    if (data.recipientMode === 'csv' && data.csvPhones.length === 0) {
      setError('Carregue um CSV com ao menos um telefone válido');
      return;
    }
    if (data.recipientMode === 'contacts' && data.selectedContactIds.length === 0) {
      setError('Selecione ao menos um contato');
      return;
    }
    setError(null);
    onNext();
  }

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex rounded-md border p-1 gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { onChange({ recipientMode: t.key }); setError(null); }}
            className={`flex-1 rounded py-1.5 text-sm font-medium transition-colors ${
              data.recipientMode === t.key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* CSV tab */}
      {data.recipientMode === 'csv' && (
        <div className="space-y-3">
          <div
            className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-input p-6 cursor-pointer hover:border-primary transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {data.csvFile ? data.csvFile.name : 'Clique para selecionar CSV'}
            </p>
          </div>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />

          {data.csvParsedHeaders.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Coluna de telefone *</Label>
                <Select value={data.phoneCol} onValueChange={handlePhoneColChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {data.csvParsedHeaders.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Coluna de nome (opcional)</Label>
                <Select value={data.nameCol} onValueChange={(v) => onChange({ nameCol: v })}>
                  <SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Nenhuma</SelectItem>
                    {data.csvParsedHeaders.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {data.csvPhones.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {data.csvPhones.length} telefone(s) detectado(s) · prévia:{' '}
              {data.csvPhones.slice(0, 3).join(', ')}
              {data.csvPhones.length > 3 && ` …+${data.csvPhones.length - 3}`}
            </p>
          )}
        </div>
      )}

      {/* Contacts tab */}
      {data.recipientMode === 'contacts' && (
        <div className="space-y-2">
          <Input
            placeholder="Buscar contatos…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="max-h-56 overflow-y-auto rounded border divide-y">
            {isLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))}
            {!isLoading && contacts.length === 0 && (
              <p className="py-4 text-center text-xs text-muted-foreground">Nenhum contato encontrado</p>
            )}
            {contacts.map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={data.selectedContactIds.includes(c.id)}
                  onChange={() => toggleContact(c.id)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">{c.name ?? c.phone}</span>
                {c.name && (
                  <span className="ml-auto font-mono text-xs text-muted-foreground">{c.phone}</span>
                )}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {data.selectedContactIds.length} selecionado(s)
          </p>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>← Voltar</Button>
        <Button onClick={handleNext}>Próximo →</Button>
      </div>
    </div>
  );
}
